import { readLoginGrantState, type LoginGrantState } from "helpers/login-grant";
import {
  CHANGE_DESTINATION_LABEL,
  type GrantCompletion,
} from "pages/Login/login-grant-flow";
import {
  confirmationScreenFor,
  type ConfirmationScreen,
} from "pages/StartLead/awaitingConfirmation";
import { verifyPathFor } from "pages/StartLead/destinations";
import { handoffFor } from "pages/StartLead/handoff";
import {
  leadFlowEvents,
  type ConfirmMethod,
  type LeadFlowEvent,
  type VerifyScreen,
} from "pages/StartLead/events";
import type {
  ConfirmedVia,
  ConfirmRefusal,
  SignInConfirmAnswer,
  SignInConfirmClient,
  SubmissionStatusClient,
} from "pages/StartLead/submissionStatus";
import { submissionIdFromSearch } from "pages/StartResume/resume";

/** The signed verify link is sent only to pre-need leads. */
const SIGNED_LINK_LEAD_KIND = "planning";

/** Where a visitor sent to verify goes: the sign-in screen with a fresh
 * grant for the submission named by `?s=`, or the interstitial when the id
 * is bad or no grant can be minted. */
export function verifyScreenFor(
  search: string,
  client: Pick<SubmissionStatusClient, "mint">,
): Promise<ConfirmationScreen> {
  const submissionId = submissionIdFromSearch(search);
  const handoff =
    submissionId === null ? null : handoffFor(search, submissionId);
  return confirmationScreenFor(
    submissionId,
    client,
    handoff === null ? undefined : SIGNED_LINK_LEAD_KIND,
    handoff,
  );
}

export type VerifyEntry =
  { kind: "grant"; grant: LoginGrantState } | { kind: "mint" };

/** What the verify route starts from: the grant the lead form handed over in
 * router state, else a grant to mint for the submission in the URL. */
export function verifyEntryFor(state: unknown): VerifyEntry {
  const grant = readLoginGrantState(state);
  return grant === null ? { kind: "mint" } : { kind: "grant", grant };
}

export type VerifyResolution =
  | Exclude<ConfirmationScreen, { kind: "interstitial" }>
  | { kind: "grant"; grant: LoginGrantState }
  /** No code can be had for this visitor and submission: the look-up form
   * for a signed-out visitor, where they type the email or phone they used. */
  | { kind: "look_up" }
  /** The submission cannot be confirmed for this visitor. */
  | { kind: "refused"; reason: ConfirmRefusal }
  /** The visitor's own submission is still being finished; ask again. */
  | { kind: "pending" };

/** What the verify route does on a visit. A visitor signed in as the
 * submission's own person is confirmed whatever the entry, told the
 * submission expired or closed, or told it is still being finished; any
 * other refusal, or a signed-out visit,
 * resumes the stored grant or mints one. Where no code can be had, a
 * signed-out visitor gets the look-up form and a signed-in one is told the
 * request is not found for their account. A signed-out visitor makes no
 * sign-in request. */
export async function resolveVerify(
  search: string,
  entry: VerifyEntry,
  signedIn: boolean,
  client: Pick<SubmissionStatusClient, "mint"> & SignInConfirmClient,
  /** Hears of what the signed-in visitor's confirmation answered. */
  report?: (event: LeadFlowEvent) => void,
): Promise<VerifyResolution> {
  const submissionId = submissionIdFromSearch(search);
  if (signedIn && submissionId !== null) {
    const answer = await client.signInConfirm(submissionId);
    report?.(signedInConfirmEventFor(answer));
    if (answer.kind === "confirmed") {
      return {
        kind: "confirmed",
        next: answer.next,
        submissionId,
        via: "signed_in",
      };
    }
    if (answer.kind === "pending") return { kind: "pending" };
    if (
      answer.kind === "refused" &&
      (answer.reason === "expired" || answer.reason === "closed")
    ) {
      return { kind: "refused", reason: answer.reason };
    }
  }
  if (entry.kind === "grant") return { kind: "grant", grant: entry.grant };
  const screen = await verifyScreenFor(search, client);
  if (screen.kind !== "interstitial") return screen;
  return signedIn
    ? { kind: "refused", reason: "not_found" }
    : { kind: "look_up" };
}

/** The page a look-up returns to once the visitor has proved who they are:
 * this verify page for its submission, or the lead form's first screen when
 * the URL names none. Undefined sends the sign-in to its default. */
export function verifyRedirectFor(search: string): string | undefined {
  const submissionId = submissionIdFromSearch(search);
  return submissionId === null
    ? undefined
    : verifyPathFor(submissionId.toLowerCase());
}

/** The router state a not-found proof carries back to the verify page, so it
 * can say so to a visitor nobody signed in. */
export const VERIFY_NOT_FOUND_STATE = { verifyNotFound: true } as const;

export function readVerifyNotFound(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as Record<string, unknown>).verifyNotFound === true
  );
}

const VERIFY_PATH =
  /^\/start\/verify\?s=[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The verify page a proof that reached nobody sends the visitor back to,
 * when the answer names it. Null for any other answer. */
export function verifyNotFoundPathOf(
  completion: GrantCompletion,
): string | null {
  if (completion.kind !== "not_found") return null;
  const path = completion.redirectPath;
  return path !== null && VERIFY_PATH.test(path) ? path : null;
}

export type RefusedCopy = {
  title: string;
  /** What the screen's main action does: start a new request, or sign out
   * and look up with another email or phone. */
  action: "start_again" | "use_different";
};

const REFUSED_COPY: Record<ConfirmRefusal, RefusedCopy> = {
  expired: { title: "This request has expired", action: "start_again" },
  closed: { title: "This request is closed", action: "start_again" },
  not_found: {
    title: "We couldn’t find this request for your account",
    action: "use_different",
  },
};

export function refusedCopyFor(reason: ConfirmRefusal): RefusedCopy {
  return REFUSED_COPY[reason];
}

const PENDING_RETRY_DELAYS_MS: readonly number[] = [2000, 4000, 8000];

/** How long to wait before asking again once the submission has answered
 * `pending` `answered` times, or null when the attempts are used up and the
 * visitor is offered another try. */
export function pendingRetryDelayMs(answered: number): number | null {
  return PENDING_RETRY_DELAYS_MS[answered - 1] ?? null;
}

export type PendingCopy = { title: string; body: string };

export function pendingCopyFor(retrying: boolean): PendingCopy {
  return retrying
    ? {
        title: "We’re finishing up your request",
        body: "One moment.",
      }
    : {
        title: "We’re still finishing up your request",
        body: "This is taking longer than usual. You can try again, or call us.",
      };
}

export const PENDING_RETRY_LABEL = "Try again";
export const START_AGAIN_LABEL = "Start again";
export const USE_DIFFERENT_LABEL = CHANGE_DESTINATION_LABEL;

/** The method that confirmed a submission without a code. */
export function confirmedMethodOf(via: ConfirmedVia): ConfirmMethod {
  return via === "signed_in" ? "signed_in" : "remembered_browser";
}

/** The analytics event for a visitor the verify route confirmed. */
export function verifyConfirmedEvent(via: ConfirmedVia): LeadFlowEvent {
  return leadFlowEvents.confirmAccepted({
    purpose: "lead_confirmation",
    surface: "verify",
    method: confirmedMethodOf(via),
  });
}

/** The analytics event for what a signed-in visitor's confirmation
 * answered. */
export function signedInConfirmEventFor(
  answer: SignInConfirmAnswer,
): LeadFlowEvent {
  switch (answer.kind) {
    case "confirmed":
      return leadFlowEvents.verifySignedInConfirm("confirmed");
    case "refused":
      return leadFlowEvents.verifySignedInConfirm("refused", answer.reason);
    case "pending":
      return leadFlowEvents.verifySignedInConfirm("pending");
    case "not_confirmed":
      return leadFlowEvents.verifySignedInConfirm("unavailable");
  }
}

/** The analytics event for an answer to a check made after `answeredBefore`
 * answers that the submission was still being finished: another wait (the
 * last one when the attempts are used up), or the confirmation the waiting
 * was for. Null for any other answer, and for a first check. */
export function verifyPendingEventFor(
  answeredBefore: number,
  resolution: VerifyResolution,
): LeadFlowEvent | null {
  if (resolution.kind === "pending") {
    const attempt = answeredBefore + 1;
    return leadFlowEvents.verifyPending({
      attempt,
      outcome: pendingRetryDelayMs(attempt) === null ? "exhausted" : "retrying",
    });
  }
  if (resolution.kind === "confirmed" && answeredBefore > 0) {
    return leadFlowEvents.verifyPending({
      attempt: answeredBefore,
      outcome: "confirmed",
    });
  }
  return null;
}

/** The screen the page is showing, for the report of it: the code screen
 * only when the page started from a grant. Null while it checks. */
export function verifyScreenOf(
  phase: "checking" | "ready" | "look_up" | "refused" | "pending",
): VerifyScreen | null {
  switch (phase) {
    case "checking":
      return null;
    case "ready":
      return "code";
    case "look_up":
    case "refused":
    case "pending":
      return phase;
  }
}

/** The screen to report now: the one on show, unless it is the one last
 * reported. */
export function verifyScreenToReport(
  last: VerifyScreen | null,
  current: VerifyScreen | null,
): VerifyScreen | null {
  return current !== last ? current : null;
}
